import axios from 'axios';
import Swal from 'sweetalert2';
import { isGrpcEnabled } from './grpc/config';
import { clearCachedFeatures } from './helpers/featureFlags';
import { buildPaywallUrl } from './helpers/paywall';
import { invalidatePlanUsage } from './helpers/planUsage';
import { grpcTransport } from './grpc/transport';

const timeout = parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 20000;

const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: timeout,
});

if (typeof window !== 'undefined') {
    window.__ACOMPANHA_GRPC__ = isGrpcEnabled();
}

const defaultAdapter = axios.getAdapter(api.defaults.adapter);

/** Reuse in-flight GET responses (React Strict Mode remounts, duplicate effects). */
const inFlightGetResponses = new Map();

function getRequestDedupeKey(config) {
    const method = String(config.method || 'get').toLowerCase();
    if (method !== 'get') {
        return null;
    }
    const url = String(config.url || '');
    const params = config.params
        ? JSON.stringify(config.params, Object.keys(config.params).sort())
        : '';
    return `${url}\0${params}`;
}

async function dispatchRequest(config) {
    if (isGrpcEnabled()) {
        try {
            const grpcResponse = await grpcTransport(config);
            if (grpcResponse != null) {
                return {
                    data: grpcResponse.data,
                    status: grpcResponse.status ?? 200,
                    statusText: grpcResponse.statusText ?? 'OK',
                    headers: grpcResponse.headers || {},
                    config,
                    request: {},
                };
            }
        } catch (err) {
            throw err;
        }
    }
    return defaultAdapter(config);
}

api.defaults.adapter = async (config) => {
    const dedupeKey = getRequestDedupeKey(config);
    if (!dedupeKey) {
        return dispatchRequest(config);
    }

    const pending = inFlightGetResponses.get(dedupeKey);
    if (pending) {
        const response = await pending;
        return { ...response, config };
    }

    const promise = dispatchRequest(config).finally(() => {
        inFlightGetResponses.delete(dedupeKey);
    });
    inFlightGetResponses.set(dedupeKey, promise);
    return promise;
};

api.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => {
        // Qualquer escrita pode mudar o consumo de cotas (cadastrar consome,
        // excluir devolve), então o cache do uso é descartado aqui em vez de em
        // cada tela.
        const method = String(response?.config?.method || '').toLowerCase();
        if (method === 'post' || method === 'put' || method === 'patch' || method === 'delete') {
            invalidatePlanUsage();
        }
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            clearCachedFeatures();

            const isOnLoginPage = window.location.pathname === '/admin/login'
                || window.location.pathname === '/authorization';

            if (!isOnLoginPage) {
                Swal.fire({
                    title: 'Sessão Encerrada',
                    text: 'Sua sessão expirou. Você será redirecionado para o login.',
                    icon: 'warning',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    timer: 3000,
                    didClose: () => {
                        window.location.href = '/admin/login';
                    },
                });
            }
        }

        // Cota do plano esgotada: a mensagem do backend já é destinada ao usuário,
        // e nenhuma tela trata 429 por conta própria.
        if (error.response && error.response.status === 429) {
            invalidatePlanUsage();
            const data = error.response.data || {};
            const message = data.message
                || data.error
                || 'Você atingiu um limite do seu plano.';

            Swal.fire({
                title: 'Limite do plano atingido',
                text: message,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ver planos',
                cancelButtonText: 'Fechar',
            }).then((result) => {
                if (result.isConfirmed) {
                    window.location.href = buildPaywallUrl({
                        returnTo: window.location.pathname,
                    });
                }
            });
        }

        return Promise.reject(error);
    },
);

export default api;
